import { createSelector } from 'reselect'
import { objectMap } from '../../../../../lib/utils/get-table-transform-schema-and-form-data'
import dayjs from 'dayjs'
import { convertData2JsonForm } from '../../../../shared/lib/convertData2JsonForm'
import { addEnumValBySchema } from '../../../../shared/lib/addEnumValBySchema'

const selectHr = state => state.hr

const selectHrForm = createSelector(selectHr, ({ form }) => form)

const selectHrEnums = createSelector(selectHr, ({ enums }) => enums)

const selectHrCancelModal = createSelector(selectHr, ({ cancelModal }) => cancelModal)

export const getCandidateId = createSelector(selectHr, ({ id }) => id)

export const getHrFormLoading = createSelector(selectHrForm, ({ isLoading }) => isLoading)

export const getHrFormError = createSelector(selectHrForm, ({ error }) => error)

export const getHrFormData = createSelector(selectHrForm, ({ data }) => data)

export const getHrStageCurrent = createSelector(
  getHrFormData,
  formData => formData?.stages?.current,
)

export const getHrEnumsData = createSelector(selectHrEnums, ({ data }) => data)

export const getVisibleFormData = createSelector(
  [getHrFormData, getHrEnumsData],
  (formData, enums) => {
    if (formData && enums) {
      const {
        created_date: createdDate,
        candidate: candidateData,
        request: requestData,
        modal: modalData,
        templates,
      } = formData

      if (!candidateData || !requestData) {
        return
      }

      const { resultObject: candidate, resultSchema: candidateFieldsSchema } =
        convertData2JsonForm(candidateData)

      const { resultObject: request, resultSchema: requestFieldsSchema } =
        convertData2JsonForm(requestData)

      const { resultObject: modal, resultSchema: modalFieldsSchema } = convertData2JsonForm(
        modalData?.fields,
      )

      const combinedGroups = {}

      if (candidateFieldsSchema?.groups) {
        Object.entries(candidateFieldsSchema.groups).forEach(([groupId, group]) => {
          combinedGroups[groupId] = { ...group }
        })
      }

      if (requestFieldsSchema?.groups) {
        Object.entries(requestFieldsSchema.groups).forEach(([groupId, group]) => {
          if (combinedGroups[groupId]) {
            combinedGroups[groupId] = {
              ...group,
              fields: [...combinedGroups[groupId].fields, ...group.fields],
            }
          } else {
            combinedGroups[groupId] = { ...group }
          }
        })
      }

      const combinedSchema = {
        type: 'object',
        properties: {
          ...candidateFieldsSchema?.properties,
          ...requestFieldsSchema?.properties,
        },
        required: [
          ...(candidateFieldsSchema?.required || []),
          ...(requestFieldsSchema?.required || []),
        ],
        groups: Object.keys(combinedGroups).length > 0 ? combinedGroups : undefined,
      }

      const combinedFormData = {
        ...candidate,
        ...request,
      }

      const enumItems = enums?.['enum'] || []

      if (enums?.['city']) {
        enumItems.city_id = Object.values(
          objectMap(enums['city'], ([key, value]) => {
            return [key, { id: parseInt(key), value: value }]
          }),
        )
      }

      if (enums?.['region']) {
        enumItems.region_id = Object.values(
          objectMap(enums['region'], ([key, value]) => {
            return [key, { id: parseInt(key), value: value }]
          }),
        )
      }

      Object.keys(enumItems).forEach(code => {
        if (
          combinedSchema?.properties.hasOwnProperty(code) ||
          modalFieldsSchema?.properties.hasOwnProperty(code)
        ) {
          const enumValues = enums?.['enum']?.[code] || {}
          const oneOf: Record<string, string>[] = []

          Object.keys(enumValues).forEach(enumValue => {
            return oneOf.push({
              const: enumValues[enumValue].id,
              title: enumValues[enumValue].value,
            })
          })

          if (combinedSchema?.properties.hasOwnProperty(code)) {
            addEnumValBySchema(combinedSchema, code, oneOf, enumValues)
          }
          if (modalFieldsSchema?.properties.hasOwnProperty(code)) {
            addEnumValBySchema(modalFieldsSchema, code, oneOf, enumValues)
          }
          if (combinedFormData.hasOwnProperty(code)) {
            combinedFormData[code] = combinedFormData[code]
              ? parseInt(combinedFormData[code]) || combinedFormData[code]
              : null
          }
          if (modal.hasOwnProperty(code)) {
            modal[code] = modal[code] ? parseInt(modal[code]) || modal[code] : null
          }
        }
      })

      return {
        candidateFieldsSchema,
        requestFieldsSchema,
        combinedSchema,
        combinedFormData,
        candidateFields: Object.keys(candidateFieldsSchema?.properties || {}),
        requestFields: Object.keys(requestFieldsSchema?.properties || {}),
        dateCreated: dayjs(createdDate).format('DD.MM.YYYY HH:mm') || '',
        candidate,
        request,
        modal: modalData ? modal : null,
        modalFieldsSchema: modalData ? modalFieldsSchema : null,
        modalTitle: modalData?.title,
        templates,
      }
    }
  },
)

export const getVisibleCancelModal = createSelector(
  [selectHrCancelModal, getHrEnumsData],
  (cancelModal, enums) => {
    if (cancelModal && enums) {
      const { fields, title } = cancelModal
      if (!fields) {
        return false
      }

      const { resultObject, resultSchema } = convertData2JsonForm(fields)

      const enumItems = enums?.['enum'] || []

      Object.keys(enumItems).forEach(code => {
        if (resultSchema?.properties.hasOwnProperty(code)) {
          const enumValues = enums?.['enum']?.[code] || {}
          const oneOf: Record<string, string>[] = []

          Object.keys(enumValues).forEach(enumValue => {
            return oneOf.push({
              const: enumValues[enumValue].id,
              title: enumValues[enumValue].value,
            })
          })

          if (resultSchema?.properties.hasOwnProperty(code)) {
            addEnumValBySchema(resultSchema, code, oneOf, enumValues)
          }

          if (resultObject.hasOwnProperty(code)) {
            resultObject[code] = resultObject[code] ? parseInt(resultObject[code]) : null
          }
        }
      })

      return {
        title,
        fields: resultObject,
        schema: resultSchema,
      }
    }
  },
)

export const isVisibleCancelModal = createSelector(
  [getHrFormData, selectHrCancelModal],
  (data, cancelModal) => {
    return (
      cancelModal &&
      data?.stages?.items &&
      data?.stages?.current < Object.values(data?.stages?.items).length - 1
    )
  },
)

const selectChatAvito = state => state.chatAvito

export const selectChatAvitoIsLoading = createSelector(
  selectChatAvito,
  ({ isLoading }) => isLoading,
)

export const selectChatAvitoData = createSelector(selectChatAvito, ({ data }) => data)

export const selectChatAvitoError = createSelector(selectChatAvito, ({ error }) => error)

export const getActiveAvitoChatId = createSelector(
  selectChatAvito,
  ({ activeChatId }) => activeChatId,
)

export const getActiveAvitoChat = createSelector(
  [selectChatAvitoData, getActiveAvitoChatId],
  (data, activeChatId) => {
    return data?.filter(chatItem => chatItem.chat_id === activeChatId)[0]
  },
)
